import { useMutation, useQuery, useQueryClient, UseQueryResult } from 'react-query';
import useLog from 'src/hooks/useLog';
import { get, post, put, destroy } from '../utils';
import type {
  SSOProvider,
  CreateSSOProviderRequest,
  UpdateSSOProviderRequest,
} from '../../../types/SSOProvider';

enum QueryKeys {
  SSOProviders = 'SSOProviders',
  SSOProvider = 'SSOProvider',
}

export function useQueryGetSSOProviders(
  organisationId: string,
): UseQueryResult<{ providers: SSOProvider[] }> {
  const log = useLog();
  return useQuery(
    [QueryKeys.SSOProviders, organisationId],
    () => get<{ providers: SSOProvider[] }>(`sso/organisation/${organisationId}/providers`),
    {
      enabled: Boolean(organisationId),
      onError: (err) => {
        if (err instanceof Error) {
          log.error(`There was a problem loading SSO providers: ${err.message}`, err);
        }
      },
    },
  );
}

export function useQueryGetSSOProvider(
  organisationId: string,
  id: string,
): UseQueryResult<SSOProvider> {
  const log = useLog();
  return useQuery(
    [QueryKeys.SSOProvider, organisationId, id],
    () => get<SSOProvider>(`sso/organisation/${organisationId}/providers/${id}`),
    {
      enabled: Boolean(organisationId && id),
      onError: (err) => {
        if (err instanceof Error) {
          log.error(`There was a problem loading the SSO provider: ${err.message}`, err);
        }
      },
    },
  );
}

export function useCreateSSOProviderMutation(organisationId: string) {
  const queryClient = useQueryClient();
  return useMutation(
    (data: CreateSSOProviderRequest) =>
      post<SSOProvider>(`sso/organisation/${organisationId}/providers`, data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries([QueryKeys.SSOProviders, organisationId]);
      },
    },
  );
}

export function useUpdateSSOProviderMutation(organisationId: string, id: string) {
  const queryClient = useQueryClient();
  return useMutation(
    (data: UpdateSSOProviderRequest) =>
      put<SSOProvider>(`sso/organisation/${organisationId}/providers/${id}`, { ...data }),
    {
      onSuccess: (updated) => {
        queryClient.setQueryData([QueryKeys.SSOProvider, organisationId, id], updated);
        queryClient.invalidateQueries([QueryKeys.SSOProviders, organisationId]);
      },
    },
  );
}

export function useDeleteSSOProviderMutation(organisationId: string, id: string) {
  const queryClient = useQueryClient();
  return useMutation(
    () => destroy(`sso/organisation/${organisationId}/providers/${id}`),
    {
      onSuccess: () => {
        queryClient.removeQueries([QueryKeys.SSOProvider, organisationId, id]);
        queryClient.invalidateQueries([QueryKeys.SSOProviders, organisationId]);
      },
    },
  );
}
